import { Component, CUSTOM_ELEMENTS_SCHEMA, HostBinding, inject } from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { DxHttpModule } from 'devextreme-angular/http';
import { loadMessages, locale } from 'devextreme/localization';
import deMessages from 'devextreme/localization/messages/de.json';
import enMessages from 'devextreme/localization/messages/en.json';
import esMessages from 'devextreme/localization/messages/es.json';
import { filter, take, tap } from 'rxjs';
import config from '../assets/config.json';
import { AuthService } from './auth/auth.service';
import * as UserManagementActions from './features/user-management/store/actions/user-management.actions';
import { SideNavOuterToolbarComponent as SideNavToolbarComponent } from './layouts';
import { SUPPORTED_LANGUAGES } from './shared/constants';
import { ScreenService } from './shared/services';
import { LocalStorageService } from './shared/services/localStorage.service';

@Component({
    standalone: true,
    imports: [RouterModule, RouterOutlet, DxHttpModule, SideNavToolbarComponent],
    selector: 'app-root',
    templateUrl: './app.html',
    styleUrl: './app.scss',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class App {
    public title = 'TruSmart Commercial';
    private readonly screen = inject(ScreenService);
    private readonly translocoService = inject(TranslocoService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly store = inject(Store);
    private readonly authService = inject(AuthService);
    fallbackLocale: string = config.i18n.fallbackLanguage;
    supportedLangs = SUPPORTED_LANGUAGES;

    @HostBinding('class') get getClass() {
        const sizeClassName = Object.keys(this.screen.sizes)
            .filter(cl => this.screen.sizes[cl])
            .join(' ');
        return `${sizeClassName} app`;
    }

    constructor() {
        loadMessages(enMessages);
        loadMessages(deMessages);
        loadMessages(esMessages);

        const storedLocale = this.localStorageService.getLocale();
        const lang = storedLocale?.slice(0, 2) ?? this.fallbackLocale.slice(0, 2);

        this.translocoService.setActiveLang(lang);

        locale(lang);

        this.translocoService.langChanges$.subscribe(l => {
            locale(l);
        });

        this.authService.initialized$
            .pipe(
                filter(initialized => initialized),
                filter(() => this.authService.isAuthenticated()),
                take(1),
                tap(() => this.store.dispatch(UserManagementActions.ensureUsersLoaded()))
            )
            .subscribe();
    }
}
